import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, Minus, Plus, ShoppingBag, TicketPercent } from "lucide-react";
import { useCart } from "../context/CartContext";
import { optionsLabel } from "../lib/options";
import api from "../lib/api";
import { imgProps } from "../lib/img";
import { usePromoPreview } from "../lib/usePromos";
import OffersSheet from "./OffersSheet";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";

const money = (n) => `$${(n || 0).toFixed(2)}`;

export default function CartDrawer() {
  const { foodCart, drawerOpen, setDrawerOpen, updateFoodQty, channel, coupon, setCoupon, addFood, soldOut, validateCart } = useCart();
  const [addons, setAddons] = useState([]);

  useEffect(() => {
    if (drawerOpen && foodCart.length) validateCart().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, foodCart.length, channel]);
  const blocked = foodCart.filter((i) => soldOut[i.product_id]);
  useEffect(() => {
    if (!drawerOpen || !foodCart.length) { setAddons([]); return; }
    const ids = foodCart.map((i) => i.product_id || i.id).join(",");
    api.get("/cart/suggestions", { params: { exclude: ids, channel } })
      .then(({ data }) => setAddons(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, foodCart.length, channel]);
  const navigate = useNavigate();
  const [offersOpen, setOffersOpen] = useState(false);
  const preview = usePromoPreview(foodCart, channel, drawerOpen);
  const subtotal = foodCart.reduce((s, i) => s + i.price * i.quantity, 0);
  const bogoSavings = foodCart.reduce((s, i) => s + (i.bogo ? Math.floor(i.quantity / 2) * i.price : 0), 0);

  const offers = preview?.offers || [];
  const appliedOffer = coupon ? offers.find((o) => o.code === coupon) : null;
  const couponValid = appliedOffer?.status === "eligible";
  const autoOffer = !couponValid ? offers.find((o) => o.auto && o.status === "eligible" && (o.type !== "spend_get_item" || (subtotal - bogoSavings) >= (o.min_subtotal || 40))) : null;
  const itemSavings = foodCart.reduce((s, i) => s + (i.on_sale && !i.bogo && i.regular_price > i.price ? (i.regular_price - i.price) * i.quantity : 0), 0);
  const activeSavings = itemSavings + bogoSavings + (couponValid ? appliedOffer.savings : (autoOffer?.savings || 0));
  const eligibleCount = offers.filter((o) => o.status === "eligible").length;
  const fd = channel === "delivery" ? preview?.free_delivery : null;

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-xl">Your food cart</SheetTitle>
          <SheetDescription className="sr-only">Review your items, apply offers and continue to checkout.</SheetDescription>
        </SheetHeader>
        {foodCart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-neutral-400 gap-3" data-testid="cart-empty">
            <ShoppingBag size={40} strokeWidth={1.2} aria-hidden="true" />
            <p className="text-sm">Your cart is empty</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-4 py-4">
              {foodCart.map((item) => (
                <div key={item.line_id} className={`flex gap-3 items-center ${soldOut[item.product_id] ? "opacity-90" : ""}`} data-testid={`cart-item-${item.product_id}`}>
                  {item.image ? <img {...imgProps(item.image, [320], "56px")} alt={item.name} width="56" height="56" className={`w-14 h-14 rounded-xl object-cover ${soldOut[item.product_id] ? "grayscale" : ""}`} />
                    : <div className="w-14 h-14 rounded-xl shrink-0 flex items-center justify-center text-[10px] font-bold text-center px-1" style={{ background: "var(--ut-panel)", color: "var(--ut-gold)" }}>Built meal</div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{item.name}</p>
                    {soldOut[item.product_id] && (
                      <p className="text-[11px] font-bold text-red-700" data-testid={`cart-sold-out-${item.product_id}`}>{soldOut[item.product_id]} — remove it to continue</p>
                    )}
                    {item.business_name && <p className="text-[10px] font-bold uppercase tracking-wide text-[#E04F33]" data-testid={`cart-store-${item.product_id}`}>{item.business_name}</p>}
                    {(optionsLabel(item.options) || item.note) && (
                      <p className="text-[11px] text-neutral-500 truncate" data-testid={`cart-options-${item.product_id}`}>
                        {[optionsLabel(item.options), item.note].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <p className="text-sm text-neutral-500">
                      ${item.price.toFixed(2)}
                      {item.on_sale && <s className="ml-1.5 text-xs text-neutral-400">${item.regular_price.toFixed(2)}</s>}
                    </p>
                    {item.bogo && (
                      <p className="text-[11px] font-bold text-green-700" data-testid={`cart-bogo-${item.product_id}`}>
                        Buy 1 Get 1 Free{Math.floor(item.quantity / 2) > 0 ? ` · ${Math.floor(item.quantity / 2)} × $0.00 (FREE)` : ""}
                        {item.quantity % 2 === 1 && (
                          <button onClick={() => updateFoodQty(item.line_id, item.quantity + 1)} data-testid={`cart-bogo-add-${item.product_id}`}
                            className="ml-2 underline">Add your free one?</button>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {soldOut[item.product_id] ? (
                      <button data-testid={`cart-remove-${item.product_id}`} onClick={() => updateFoodQty(item.line_id, 0)}
                        className="text-xs font-bold text-white bg-neutral-800 hover:bg-black px-3 py-1.5 rounded-full">Remove</button>
                    ) : (<>
                    <button data-testid={`cart-decrease-${item.product_id}`} onClick={() => updateFoodQty(item.line_id, item.quantity - 1)}
                      className="w-7 h-7 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-neutral-200"><Minus size={13} /></button>
                    <span className="text-sm font-bold w-5 text-center" data-testid={`cart-qty-${item.product_id}`}>{item.quantity}</span>
                    <button data-testid={`cart-increase-${item.product_id}`} onClick={() => updateFoodQty(item.line_id, item.quantity + 1)}
                      className="w-7 h-7 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-neutral-200"><Plus size={13} /></button>
                    </>)}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t pt-4 space-y-3">
              {fd && (
                <div className="bg-neutral-50 rounded-xl px-4 py-3" data-testid="free-delivery-progress">
                  {fd.unlocked ? (
                    <p className="text-xs font-bold text-green-600" data-testid="free-delivery-unlocked">You've unlocked {fd.label} 🎉</p>
                  ) : (
                    <>
                      <p className="text-xs font-bold text-neutral-700" data-testid="free-delivery-remaining">
                        Add {money(fd.remaining)} more for {fd.label}
                      </p>
                      <div className="h-1.5 bg-neutral-200 rounded-full mt-2 overflow-hidden"
                        role="progressbar" aria-valuenow={Math.round(((fd.threshold - fd.remaining) / fd.threshold) * 100)} aria-valuemin={0} aria-valuemax={100}>
                        <div className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${Math.min(100, ((fd.threshold - fd.remaining) / fd.threshold) * 100)}%` }} />
                      </div>
                    </>
                  )}
                </div>
              )}

              <button data-testid="offers-coupons-row" onClick={() => setOffersOpen(true)}
                className="w-full flex items-center justify-between bg-white border border-dashed border-[#E04F33]/50 rounded-xl px-4 py-3 hover:bg-[#E04F33]/5 transition-colors">
                <span className="flex items-center gap-2 text-sm font-bold">
                  <TicketPercent size={16} className="text-[#E04F33]" />
                  {couponValid ? (
                    <span className="text-green-700" data-testid="cart-coupon-applied">{coupon} applied — saving {money(appliedOffer.savings)}</span>
                  ) : eligibleCount > 0 ? (
                    <span data-testid="cart-offers-count">Offers & Coupons · {eligibleCount} available</span>
                  ) : (
                    <span>Offers & Coupons</span>
                  )}
                </span>
                <ChevronRight size={16} className="text-neutral-400" />
              </button>

              {coupon && appliedOffer && !couponValid && (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5" data-testid="cart-coupon-warning">
                  <p className="text-xs font-semibold text-amber-800">{coupon}: {appliedOffer.reason}</p>
                  <button data-testid="cart-coupon-remove" onClick={() => setCoupon("")} className="text-xs underline text-amber-700 shrink-0 ml-2">Remove</button>
                </div>
              )}

              {addons.length > 0 && (
                <div data-testid="cart-addon-suggestions">
                  <p className="text-[11px] font-bold text-neutral-500 mb-1.5">Goes well with</p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {addons.map((p) => (
                      <button key={p.id} data-testid={`cart-addon-add-${p.id}`}
                        onClick={() => { addFood(p); toast.success(`${p.name} added`); }}
                        className="shrink-0 flex items-center gap-2 border rounded-full pl-1 pr-3 py-1 bg-white hover:border-[#E04F33] transition-colors">
                        <img {...imgProps(p.image, [320], "28px")} alt={p.name} width="28" height="28" className="w-7 h-7 rounded-full object-cover" loading="lazy" />
                        <span className="text-[11px] font-semibold whitespace-nowrap max-w-[110px] truncate">{p.name}</span>
                        <span className="text-[11px] font-bold text-[#E04F33] whitespace-nowrap">+ {money(p.display_price)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {preview?.unlock?.suggestions?.length > 0 && (
                <div data-testid="cart-unlock-suggestions">
                  <p className="text-[11px] font-bold text-neutral-500 mb-1.5">
                    Add {money(preview.unlock.remaining)} more — {preview.unlock.label}
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {preview.unlock.suggestions.slice(0, 4).map((p) => (
                      <button key={p.id} data-testid={`cart-unlock-add-${p.id}`}
                        onClick={() => { addFood(p); toast.success(`${p.name} added`); }}
                        className="shrink-0 flex items-center gap-2 border rounded-full pl-1 pr-3 py-1 bg-white hover:border-[#E04F33] transition-colors">
                        <img {...imgProps(p.image, [320], "28px")} alt={p.name} width="28" height="28" className="w-7 h-7 rounded-full object-cover" loading="lazy" />
                        <span className="text-[11px] font-semibold whitespace-nowrap max-w-[110px] truncate">{p.name}</span>
                        <span className="text-[11px] font-bold text-[#E04F33] whitespace-nowrap">+ {money(p.display_price)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {activeSavings > 0 && (
                <p className="text-xs font-bold text-green-600 text-center" data-testid="cart-total-savings">
                  You're saving {money(activeSavings)} on this order 🎉
                </p>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Subtotal ({channel.replace("_", "-")})</span>
                <span className="font-bold" data-testid="cart-subtotal">${(subtotal - bogoSavings).toFixed(2)}</span>
              </div>
              {blocked.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="cart-sold-out-block">
                  <p className="text-xs font-bold text-red-800">{blocked.map((b) => b.name).join(", ")} {blocked.length === 1 ? "is" : "are"} sold out today.</p>
                  <p className="text-[11px] text-red-700 mt-0.5">Remove {blocked.length === 1 ? "it" : "them"} to continue — we never swap a dish for you.</p>
                </div>
              )}
              <button data-testid="cart-checkout-button" disabled={blocked.length > 0}
                onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}
                className="w-full py-3.5 rounded-full bg-[#E04F33] hover:bg-[#C94228] disabled:bg-neutral-300 text-white font-bold transition-colors">
                {blocked.length > 0 ? "Remove sold-out items to continue" : "Go to checkout"}
              </button>
            </div>
          </>
        )}
        <OffersSheet open={offersOpen} onOpenChange={setOffersOpen} />
      </SheetContent>
    </Sheet>
  );
}
